import { Link } from 'react-router-dom';
import { useDocumentHead } from '@/hooks/use-document-head';
import { ScrollReveal } from '@/components/ui/scroll-reveal';

const BLOG_POSTS = [
  {
    slug: '5-signs-office-needs-professional-cleaning',
    title: '5 Signs Your Office Needs Professional Cleaning',
    date: 'April 10, 2026',
    excerpt: 'Dust buildup, mystery odors, and employee complaints are just the beginning. Here are the five unmistakable signs it\'s time to call in a professional crew.',
    image: '/images/pages/blog-post-1-messy-office.png',
    tag: 'COMMERCIAL',
  },
  {
    slug: 'difference-between-cleaning-and-disinfecting',
    title: 'The Difference Between Cleaning and Disinfecting',
    date: 'April 3, 2026',
    excerpt: 'Cleaning removes dirt. Disinfecting kills germs. Most people think they\'re doing both — but they\'re not. Here\'s what actually matters for your health.',
    image: '/images/pages/blog-post-2-cleaning-vs-disinfecting.png',
    tag: 'HEALTH',
  },
  {
    slug: 'central-florida-businesses-switching-health-based-cleaning',
    title: 'Why Central Florida Businesses Are Switching to Health-Based Cleaning',
    date: 'March 27, 2026',
    excerpt: 'Post-pandemic awareness changed the game. Facility managers across Orlando are demanding more than "looks clean." They want proof. Here\'s what\'s driving the shift.',
    image: '/images/pages/blog-post-3-health-based-cleaning.png',
    tag: 'INDUSTRY',
  },
];

export default function BlogPage() {
  useDocumentHead(
    'Cleaning Tips Blog | Sonz of Thunder Services',
    'Cleaning tips, guides, and news for Orlando & Central Florida homes and businesses from Sonz of Thunder Services (SOTSVC).',
  );
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px] clip-diag-bottom overflow-hidden">
        <img src="/images/pages/blog-hero.png" alt="" className="absolute inset-0 w-full h-full object-cover ken-burns" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(15,15,30,.92) 0%, rgba(15,15,30,.6) 60%, rgba(0,180,216,.2) 100%)' }} />
        <div className="absolute inset-0 opacity-20 grid-pattern" />
        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full fade-in">
            <div className="flex items-center gap-4 mb-6">
              <span className="h-[3px] w-16 bg-thunder" />
              <span className="text-thunder font-display tracking-[0.4em] text-xs">// KNOWLEDGE BASE</span>
            </div>
            <h1 className="font-display font-bold text-white text-4xl sm:text-6xl md:text-8xl leading-[0.88]">
              THE <span className="text-thunder">BLOG</span>
            </h1>
            <p className="text-white/80 text-lg sm:text-xl max-w-xl mt-6 font-light">
              Industry insights, cleaning tips, and the science behind a spotless space.
            </p>
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="relative py-28 bg-charcoal-deep overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {BLOG_POSTS.map((post, i) => (
              <ScrollReveal key={post.slug} delay={i * 100}>
                <article className="bg-charcoal border border-electric/20 overflow-hidden group hover:-translate-y-2 transition-all duration-300 h-full flex flex-col">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-charcoal to-transparent" />
                    <div className="absolute top-4 left-4 bg-electric text-charcoal-deep font-display font-bold px-3 py-1 tracking-[0.2em] text-[10px]">
                      {post.tag}
                    </div>
                  </div>
                  <div className="p-8 flex flex-col flex-grow">
                    <div className="text-white/40 font-display tracking-[0.2em] text-xs mb-3">{post.date.toUpperCase()}</div>
                    <h2 className="font-display font-bold text-white text-2xl leading-tight mb-4 group-hover:text-thunder transition">
                      {post.title}
                    </h2>
                    <p className="text-white/60 leading-relaxed mb-6 flex-grow">{post.excerpt}</p>
                    <div className="font-display text-electric text-sm tracking-[0.3em] flex items-center gap-2 group-hover:text-thunder transition">
                      READ MORE <span className="group-hover:translate-x-2 transition-transform">→</span>
                    </div>
                  </div>
                </article>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 bg-charcoal overflow-hidden">
        <ScrollReveal className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display font-bold text-white text-4xl sm:text-5xl leading-[0.9] mb-6">
            HAVE A CLEANING <span className="text-thunder">QUESTION?</span>
          </h2>
          <p className="text-white/60 text-lg mb-8 max-w-xl mx-auto">
            Skip the research. Call us directly and get expert advice from a team that's been doing this for over a decade.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('openContactForm'))}
              className="bg-electric hover:bg-electric-bright text-charcoal-deep font-display font-bold px-10 py-4 text-sm tracking-[0.3em] transition"
            >
              ASK US ANYTHING →
            </button>
            <a
              href="tel:4074616039"
              className="border-2 border-thunder text-thunder hover:bg-thunder hover:text-charcoal-deep font-display font-bold px-10 py-4 text-sm tracking-[0.3em] transition"
            >
              (407) 461-6039
            </a>
          </div>
        </ScrollReveal>
      </section>
    </div>
  );
}
